// Who the advertising pixel may load for. (#812)
//
// US visitors only, by allowlist. Chaptery sells to the US and Canada, and the Reddit
// campaign targets the US, so the pixel only needs to cover the US. Staying inside the
// US keeps it outside two regimes a pixel would otherwise pull us into:
//
//   - EU/UK. The pixel sets cookies, which is non-essential storage needing prior
//     consent under ePrivacy and PECR, and monitoring an EU visitor's behaviour is
//     itself enough to bring a non-EU company under GDPR Article 3(2). Not loading it
//     is simpler and stronger than a consent banner.
//   - Quebec. Law 25 section 8.1 has no size threshold and reads as profiling off by
//     default. Quebec cannot be told apart from Toronto by timezone
//     (America/Montreal is an alias of America/Toronto), so all of Canada is out.
//
// The US needs no banner at this size: CCPA/CPRA applies above $26.6M revenue or
// 100,000 California consumers a year, and the other state laws are further off.
//
// An allowlist rather than a blocklist, so a new country is added by someone deciding
// to add it, not by nobody noticing it was never excluded.

/**
 * Timezone, not an IP lookup. Placing a visitor by IP means sending their address to a
 * third party, a strange thing to do in order to protect their privacy, and it puts a
 * network call in front of the decision. The browser already knows its timezone.
 *
 * It is not precise: a VPN or a traveller reads as wherever the clock says. With an
 * allowlist every imprecision fails the safe way. An American abroad is not measured,
 * which costs one attribution; a German at home is never measured, which is the point.
 */
const US_TIMEZONES = new Set([
  // Mainland
  'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville',
  'America/Kentucky/Monticello', 'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Indiana/Marengo',
  'America/Indiana/Petersburg', 'America/Indiana/Vevay', 'America/Indiana/Tell_City',
  'America/Indiana/Knox', 'America/Chicago', 'America/Menominee',
  // What Chrome's ICU actually reports for America/Indiana/Indianapolis and
  // America/Kentucky/Louisville: the older names, not the canonical ones above.
  'America/Indianapolis', 'America/Louisville',
  'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah', 'America/Denver', 'America/Boise',
  'America/Phoenix', 'America/Los_Angeles',
  // Alaska and Hawaii
  'America/Anchorage', 'America/Juneau', 'America/Sitka', 'America/Metlakatla',
  'America/Yakutat', 'America/Nome', 'America/Adak', 'Pacific/Honolulu',
  // Territories
  'America/Puerto_Rico', 'America/St_Thomas', 'Pacific/Guam', 'Pacific/Saipan',
  'Pacific/Pago_Pago',
]);

/** True only when the browser reports a US timezone. Unknown fails closed. */
export function adRegionAllowed(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !!tz && US_TIMEZONES.has(tz);
  } catch {
    return false;
  }
}

/**
 * Global Privacy Control: a do-not-sell-or-share signal browsers and extensions send.
 * Not required of us at this size, but it is a person telling us, in the one channel
 * they have, that they do not want this. Honouring it costs one conditional and no
 * interface.
 */
export function gpcEnabled(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/** Region and the visitor's own signal. Both must allow it. */
export function adTrackingAllowed(): boolean {
  return adRegionAllowed() && !gpcEnabled();
}
